/**
 * Calendar-date helpers for reports.
 * Keep dates as YYYY-MM-DD strings — never route them through UTC (toISOString).
 */

export function pad2(value) {
  return String(value).padStart(2, '0');
}

/** Local calendar today as YYYY-MM-DD */
export function localTodayISO() {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** Format a local Date (or Y/M/D parts) as YYYY-MM-DD using local calendar fields */
export function localDateToISO(date) {
  if (!date || Number.isNaN(date.getTime?.() ?? NaN)) {
    return '';
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * Normalize any common report date input to YYYY-MM-DD (calendar date only).
 * Does not use Date#toISOString (UTC), which shifts the day in +UTC zones.
 */
export function toReportDateISO(value) {
  if (value == null || value === '') {
    return '';
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return localDateToISO(value);
  }

  const raw = String(value).trim();
  if (!raw) {
    return '';
  }

  // Already ISO date (optionally with time)
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  // MM/DD/YYYY or M/D/YYYY
  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  // DD-MM-YYYY
  const dmyDash = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyDash) {
    const [, day, month, year] = dmyDash;
    return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  return '';
}

/** Display YYYY-MM-DD (or other) as MM/DD/YYYY without UTC shift */
export function formatReportDateUS(value) {
  const iso = toReportDateISO(value);
  if (!iso) {
    return '';
  }
  const [year, month, day] = iso.split('-');
  return `${month}/${day}/${year}`;
}
